import React, { useEffect, useState } from "react";
import { GoogleMap, MarkerF, useJsApiLoader } from "@react-google-maps/api";
import { selectLocationsByState } from "../features/rapidstor/rapidstorApiSlice";
import { useSelector } from "react-redux";
import classes from "./Map.module.scss";

export const options = {
  apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  center: {
    lat: -31.941530271728467,
    lng: 115.850385613311,
  },
  zoom: 9,
};

interface Props {
  region?: string;
}

function Map({ region }: Props) {
  const locations = useSelector((state) => selectLocationsByState(state, region));

  const [map, setMap] = useState<google.maps.Map>();

  const mapLoaded = (map: google.maps.Map) => {
    setMap(map);
  };

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: options.apiKey,
  });

  // useEffect justification: used to synchronize with an external system (Google Maps)
  useEffect(() => {
    if (!map) {
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    for (const location of locations) {
      bounds.extend({ lat: location.latitude, lng: location.longitude });
    }
    map.fitBounds(bounds);
  }, [map, locations]);

  const mapStyle = [
    {
      elementType: "geometry",
      stylers: [
        {
          color: "#f5f5f5",
        },
      ],
    },
    {
      elementType: "labels.icon",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      elementType: "labels.text.fill",
      stylers: [
        {
          color: "#616161",
        },
      ],
    },
    {
      elementType: "labels.text.stroke",
      stylers: [
        {
          color: "#f5f5f5",
        },
      ],
    },
    {
      featureType: "administrative.land_parcel",
      elementType: "labels.text.fill",
      stylers: [
        {
          color: "#bdbdbd",
        },
      ],
    },
    {
      featureType: "poi",
      elementType: "geometry",
      stylers: [
        {
          color: "#eeeeee",
        },
      ],
    },
    {
      featureType: "poi",
      elementType: "labels.text.fill",
      stylers: [
        {
          color: "#757575",
        },
      ],
    },
    {
      featureType: "poi.park",
      elementType: "geometry",
      stylers: [
        {
          color: "#e5e5e5",
        },
      ],
    },
    {
      featureType: "poi.park",
      elementType: "labels.text.fill",
      stylers: [
        {
          color: "#9e9e9e",
        },
      ],
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [
        {
          color: "#ffffff",
        },
      ],
    },
    {
      featureType: "road.arterial",
      elementType: "labels.text.fill",
      stylers: [
        {
          color: "#757575",
        },
      ],
    },
    {
      featureType: "road.highway",
      elementType: "geometry",
      stylers: [
        {
          color: "#dadada",
        },
      ],
    },
    {
      featureType: "road.highway",
      elementType: "labels.text.fill",
      stylers: [
        {
          color: "#616161",
        },
      ],
    },
    {
      featureType: "road.local",
      elementType: "labels.text.fill",
      stylers: [
        {
          color: "#9e9e9e",
        },
      ],
    },
    {
      featureType: "transit.line",
      elementType: "geometry",
      stylers: [
        {
          color: "#e5e5e5",
        },
      ],
    },
    {
      featureType: "transit.station",
      elementType: "geometry",
      stylers: [
        {
          color: "#eeeeee",
        },
      ],
    },
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [
        {
          color: "#c9c9c9",
        },
      ],
    },
    {
      featureType: "water",
      elementType: "labels.text.fill",
      stylers: [
        {
          color: "#9e9e9e",
        },
      ],
    },
  ];

  return isLoaded ? (
    <GoogleMap
      center={options.center}
      zoom={options.zoom}
      mapContainerClassName={classes.map}
      onLoad={mapLoaded}
      options={{ styles: mapStyle }}
    >
      {locations.map((location) => (
        <MarkerF
          key={location.locationCode}
          position={{ lat: location.latitude, lng: location.longitude }}
        />
      ))}
      <></>
    </GoogleMap>
  ) : (
    <>Loading map...</>
  );
}

export default Map;
